import { useTranslate } from '@tolgee/react';
import { ReturnActionsProps } from './types';
import { Button, message } from 'antd';
import { confirmReturn, disputeReturn, KwaiOperate, refuse } from 'services/api/return';
import { useRequest } from 'ahooks';
import { getApiErrorCodeMsg } from 'services/api/utils/errorCode';
import StatusButton from 'components/StatusButton';
import { formatNumber } from 'utils/numberFormat';
import useModal from 'hooks/useModal';
import { RefuseFormModal, RefuseFormModalProps } from 'components/Refund/Refuse';

export const ReturnActionKwai = (props: ReturnActionsProps) => {
  const {
    row: {
      unit,
      refundAmount,
      internalStoreId,
      externalStoreId,
      orderSn,
      returnSn,
      operate = [],
      returnItemData,
    },
    platform,
    refreshList,
  } = props;

  const { t } = useTranslate();

  const { runAsync: confirmSubmit } = useRequest(
    (operate: KwaiOperate) =>
      confirmReturn({
        externalStoreId,
        externalTradeId: orderSn,
        returnSn,
        externalType: platform,
        operate,
      }),
    {
      manual: true,
      onSuccess() {
        refreshList();
      },
      onError(err: any) {
        message.error(getApiErrorCodeMsg(err.response));
      },
    },
  );

  const { visible, show, hide, meta } = useModal<Omit<RefuseFormModalProps, 'onSubmit'>>();

  return (
    <>
      {operate.includes('CONFIRM_RETURN_REFUND') && (
        <StatusButton
          size="small"
          popConfirmProps={{
            title: t('scene.orderWorkspace.returnRefundTab.dialog.confirmAcceptReturn'),
          }}
          onClick={() => confirmSubmit('CONFIRM_RETURN_REFUND')}
        >
          {t('scene.orderWorkspace.returnRefundTab.action.agreeReturn')}
        </StatusButton>
      )}
      {operate.includes('CONFIRM_ONLY_REFUND') && (
        <StatusButton
          size="small"
          popConfirmProps={{
            title: t('scene.orderWorkspace.returnRefundTab.dialog.confirmAcceptRefund'),
          }}
          onClick={() => confirmSubmit('CONFIRM_ONLY_REFUND')}
        >
          {t('scene.orderWorkspace.returnRefundTab.action.agreeRefund')}
        </StatusButton>
      )}
      {operate.includes('FULL_REFUND') && (
        <StatusButton
          size="small"
          popConfirmProps={{
            title: t('scene.orderWorkspace.returnRefundTab.detail.hint.fullRefund', {
              money: `${unit}${formatNumber(refundAmount, '0,0.00')}`,
            }),
          }}
          onClick={() => confirmSubmit('FULL_REFUND')}
        >
          {t('scene.orderWorkspace.returnRefundTab.action.changeToRefundOnlyAndApprove')}
        </StatusButton>
      )}
      {operate.includes('REFUSE') && (
        <>
          <Button
            size="small"
            onClick={() => {
              show({
                internalStoreId,
                initialValues: {
                  externalStoreId,
                  returnSn,
                  returnOrderItemId: returnItemData.map((_) => _.returnOrderItemId) as any,
                  image: [],
                  reasonId: undefined,
                  comment: '',
                  operate: 'REFUSE',
                },
              });
            }}
          >
            {t('scene.orderWorkspace.returnRefundTab.list.action.reject')}
          </Button>
          {meta && (
            <RefuseFormModal
              visible={visible}
              onCancel={() => hide()}
              {...meta}
              onSubmit={(values, helpers) => {
                const { image, reasonId = '', comment = '', ...others } = values;

                return refuse({
                  ...others,
                  reasonId,
                  comment,
                  image: image
                    .map((file) => file.response?.data?.url || file.url)
                    .filter((_) => _) as string[],
                })
                  .then(() => {
                    refreshList();

                    message.success(
                      t('scene.orderWorkspace.returnRefundTab.detail.status.rejected'),
                    );

                    hide();

                    helpers.resetForm();
                  })
                  .catch((e) => {
                    message.error(
                      getApiErrorCodeMsg(e.response, 'ViewRefund.拒绝失败，请稍后再试'),
                    );
                  });
              }}
            />
          )}
        </>
      )}
      {operate.includes('DISPUTE') && (
        <StatusButton
          size="small"
          onClick={() => {
            return disputeReturn({
              platform: 'KWAI',
              returnSn,
              operate: 'DISPUTE',
            })
              .then(() => {
                refreshList?.();
                message.success(t('common.feedback.saveSuccess'));
              })
              .catch(() => {
                message.error(t('common.feedback.saveFailedPleaseRetry'));
              });
          }}
        >
          {t('scene.orderWorkspace.returnRefundTab.action.requestPlatformIntervention')}
        </StatusButton>
      )}
    </>
  );
};
